import { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import tw from 'twin.macro';

import { Button } from '@components/Button';
import { Switch } from '@account_module/components/onboarding/Switch';
import { DuplicateShow } from '@account_module/components/onboarding/MegaphoneDuplicateShow';
import { useMegaphoneNetworksShows } from '@account_module/hooks/useMegaphoneNetworksShows';
import { useMegaphoneOnboardContext } from '@account_module/context/MegaphoneOnboardContext';
import { MegaphoneShowType, DuplicateShowType } from '@account_module/types/Onboarding';
import { useIntegration } from '@account_module/hooks/useIntegrations';
import { createIntegration } from '@account_module/services/integrations';
import { IntegrationParamsType } from '@account_module/types/Integration';
import { Loader } from '@components/Loader';
import loaderImage from '@assets/images/loader.png';
import { getIdsPayloadString } from '@account_module/utils/integrationUtils';

const LoaderContainer = tw.div`w-full h-full flex flex-col items-center justify-center`;
const LoaderText = tw.span`text-primary text-[24px] font-bold mt-[32px]`;

const Flex = tw.div`flex items-center`;
const FlexEnd = tw(Flex)`justify-end gap-2`;
const Title = tw.h3`text-left text-[24px] mb-[12px]`;
const Subtitle = tw.p`text-left text-base leading-[150%] text-text-muted-2 max-w-[747px]`;
const Container = tw.div`h-full p-[24px] flex flex-col gap-[24px]`;
const List = tw.div`rounded-lg border border-solid border-muted4 overflow-hidden`;
const ListBody = tw.div`w-full h-[calc(100% - 44px)] overflow-y-scroll`;
const ListHeadTitle = tw.span`text-[14px] text-text-muted-2 font-bold leading-[150%] whitespace-nowrap px-4 py-3`;
const SuccessMessage = tw.div`text-base text-[#247260] bg-[#E4F6F2] font-semibold p-4 rounded-lg`;
const OuterRow = tw.div`w-full grid grid-cols-[317px_1fr]`;
const InnerRow = tw.div`w-full grid grid-cols-[250px_250px_1fr]`;
const InviteUser = tw.p`text-left text-text-muted-2 text-sm leading-[150%] cursor-pointer`;
const TotalShowsCount = tw.span`text-[18px] text-text-muted-2 font-bold leading-[140%]`;
const NextButton = tw(
  Button.Primary
)`disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center w-[110px] min-w-[110px] bg-[#2A3FCB]`;

const BackButton = tw(
  NextButton
)`text-primary border border-solid border-[rgba(42, 63, 203, 0.50)] bg-transparent w-[84px] min-w-[84px]`;

const LoadingSpinner = () => (
  <img tw='animate-spin' width='73' height='73' src={loaderImage} alt='loader' />
);

type CtaType = 'next' | 'back';

const CTA_TYPE = {
  NEXT: 'next' as CtaType,
  BACK: 'back' as CtaType,
};

export const DuplicateShowsList = ({
  onSuccess,
  onGoBack,
}: {
  onSuccess: () => void;
  onGoBack: () => void;
}) => {
  const { t } = useTranslation();
  const {
    integrationId,
    token,
    networksToInclude,
    showsToInclude,
    showsToExclude,
    setShowsToInclude,
    setShowsToExclude,
    setIsInviteModalVisible,
  } = useMegaphoneOnboardContext();
  const [selectedCTA, setSelectedCTA] = useState<CtaType>(CTA_TYPE.NEXT);

  const { showsResultsByNetworks, isLoading, filteredShows, adFreeOrNonMainFeedShows } =
    useMegaphoneNetworksShows(networksToInclude, token);
  const { updateIntegration, isUpdateLoading } = useIntegration(integrationId);
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false);

  const getDuplicatedShows = (shows: MegaphoneShowType[]): DuplicateShowType[] => {
    const duplicated = shows
      ?.reduce(
        (
          acc: DuplicateShowType[],
          { id, title, imageFile, networkId, adFree, mainFeed, ...props }
        ) => {
          let _show = acc.find((show: DuplicateShowType) => title === show.title);

          if (!_show) {
            acc.push((_show = { id, title, imageFile, networkId, duplicates: [] }));
          }
          _show?.duplicates.push({ id, title, imageFile, networkId, adFree, mainFeed, ...props });

          return acc;
        },
        []
      )
      .filter((shows) => shows.duplicates.length > 1);

    return duplicated;
  };

  const showsWithDuplicates = useMemo((): DuplicateShowType[] => {
    if (isLoading) return [];

    return getDuplicatedShows(filteredShows);
  }, [isLoading]);

  const showsToBeProcessedCount = useMemo(() => {
    if (!filteredShows) return 0;

    return showsToExclude?.length
      ? filteredShows.length - showsToExclude?.length
      : filteredShows.length;
  }, [showsToExclude, showsToInclude, showsResultsByNetworks]);

  const duplicatedShowsIdCount = useMemo(
    () =>
      !showsWithDuplicates?.length
        ? 0
        : showsWithDuplicates
            ?.filter((show) => show?.duplicates?.length > 1)
            .map((show) => show.duplicates.length)
            .reduce((acc, current) => (acc as number) + (current as number)),
    [showsWithDuplicates]
  );

  const handleBulkIncludeExclude = (val: boolean) => {
    const allShowsIds: string[] = [];
    showsWithDuplicates?.map((show) => show?.duplicates.map((show) => allShowsIds.push(show.id)));

    if (val) {
      setShowsToInclude(allShowsIds);
      setShowsToExclude([]);
    } else {
      setShowsToInclude([]);
      setShowsToExclude(allShowsIds);
    }
  };

  const fetchingShowsByNetworkProgress = useMemo(() => {
    const loadedNetworksCount = showsResultsByNetworks.filter(
      (network) => !network.isLoading
    ).length;

    return Math.round((loadedNetworksCount / showsResultsByNetworks.length) * 100);
  }, [showsResultsByNetworks]);

  const onSubmit = async () => {
    setIsSubmiting(true);
    const requestData: IntegrationParamsType = {
      progress_step: 2,
      include_shows: null,
      exclude_networks: null,
      include_networks: getIdsPayloadString(networksToInclude),
      exclude_shows: getIdsPayloadString([
        ...showsToExclude,
        ...adFreeOrNonMainFeedShows.map((show: MegaphoneShowType) => show.id),
      ]),
    };

    try {
      if (integrationId) {
        if (updateIntegration) {
          await updateIntegration(requestData);
          await createIntegration(integrationId, { progress_step: 3 });
          await updateIntegration({ progress_step: 3 });
          setIsSubmiting(false);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      onSuccess();
    }
  };

  const handlGoBack = async () => {
    if (updateIntegration) {
      const data = await updateIntegration({ progress_step: 1 });

      if (data) {
        onGoBack();
      }
    }
  };

  // Initially set all shows to "include"
  useEffect(() => {
    if (isLoading || !showsWithDuplicates.length) return;

    handleBulkIncludeExclude(true);
  }, [showsWithDuplicates, isLoading]);

  if (isLoading) {
    return (
      <LoaderContainer>
        <LoadingSpinner />
        <LoaderText>
          {t('account_module.onboard_page.duplicate_shows_list.loading_copy')}
        </LoaderText>
        <span>{fetchingShowsByNetworkProgress}%</span>
      </LoaderContainer>
    );
  }

  return (
    <Container>
      <div>
        <Title>{t('account_module.onboard_page.duplicate_shows_list.title')}</Title>
        <Subtitle>{t('account_module.onboard_page.duplicate_shows_list.subtitle')}</Subtitle>
      </div>

      {!showsWithDuplicates.length && !isLoading ? (
        <SuccessMessage>
          {t('account_module.onboard_page.duplicate_shows_list.no_duplicates')}
        </SuccessMessage>
      ) : (
        <List>
          <OuterRow tw='border-b border-solid border-card-border bg-selected'>
            <ListHeadTitle>
              {t('account_module.onboard_page.duplicate_shows_list.shows_count', {
                count: showsWithDuplicates.length,
              })}
            </ListHeadTitle>
            <InnerRow>
              <ListHeadTitle>
                {t('account_module.onboard_page.duplicate_shows_list.ids_count', {
                  count: duplicatedShowsIdCount,
                })}
              </ListHeadTitle>
              <ListHeadTitle>
                {t('account_module.onboard_page.duplicate_shows_list.network_id')}
              </ListHeadTitle>

              <ListHeadTitle tw='flex items-center justify-end gap-2'>
                {showsToInclude?.length === duplicatedShowsIdCount
                  ? t('account_module.onboard_page.duplicate_shows_list.action.exclude_all')
                  : t('account_module.onboard_page.duplicate_shows_list.action.include_all')}
                <Switch
                  onChange={(val) => handleBulkIncludeExclude(val as boolean)}
                  checked={showsToInclude?.length === duplicatedShowsIdCount}
                />
              </ListHeadTitle>
            </InnerRow>
          </OuterRow>

          <ListBody>
            {showsWithDuplicates?.map((show, index) => <DuplicateShow key={index} show={show} />)}
          </ListBody>
        </List>
      )}
      <Flex tw='justify-between'>
        <InviteUser onClick={() => setIsInviteModalVisible(true)}>
          <Trans i18nKey='account_module.onboard_page.credentials_form.invite_coworker_link'>
            <span tw='text-accent underline'>Invite a team member</span> to help you with
            onboarding.
          </Trans>
        </InviteUser>

        <FlexEnd>
          <FlexEnd tw='gap-4'>
            <TotalShowsCount>
              {t('account_module.onboard_page.duplicate_shows_list.total_shows_count', {
                count: showsToBeProcessedCount,
              })}
            </TotalShowsCount>
            <BackButton
              disabled={isUpdateLoading || isSubmiting}
              onClick={() => {
                setSelectedCTA(CTA_TYPE.BACK);
                handlGoBack();
              }}>
              {(isUpdateLoading || isSubmiting) && selectedCTA === CTA_TYPE.BACK ? (
                <Loader size='sm' theme='light' />
              ) : (
                t('common.back')
              )}
            </BackButton>
            <NextButton
              onClick={() => {
                setSelectedCTA(CTA_TYPE.NEXT);
                onSubmit();
              }}
              disabled={isUpdateLoading || isSubmiting}>
              {(isUpdateLoading || isSubmiting) && selectedCTA === CTA_TYPE.NEXT ? (
                <Loader size='sm' theme='dark' />
              ) : (
                t('account_module.onboard_page.duplicate_shows_list.action.proccess')
              )}
            </NextButton>
          </FlexEnd>
        </FlexEnd>
      </Flex>
    </Container>
  );
};
